import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronRight, ChevronLeft, ArrowLeft, X } from 'lucide-react';
import theme from '../../constants/theme';

const caseStudies = [
  {
    id: 1,
    title: 'AI-Powered Automated Soccer Camera Tracking',
    tag: 'Computer Vision',
    tagColor: '#c60664',
    metric: 'real-time',
    image: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=800&q=80',
    problem:
      'Amateur clubs, academies, and small-scale sports productions lack the resources to operate professional camera crews, resulting in missed action, inconsistent footage, and limited access to high-quality match recordings for analysis and broadcasting.',
    solution:
      'Designed and developed an AI-powered automated camera tracking system capable of real-time player and ball detection from live video streams. Implemented intelligent pan and zoom logic to dynamically follow gameplay. Optimized the system for edge deployment on NVIDIA Jetson Nano using model quantization and engine-format conversion, enabling fully offline, low-latency performance without cloud dependency.',
    outcome:
      'Delivered broadcast-quality automated footage with zero manual intervention. Enabled real-time processing with no delays, making the solution scalable and cost-effective. The system empowers clubs and coaches with high-quality match recordings for tactical analysis and supports automated highlight generation and affordable sports production.',
    youtubeId: 'JuJsjiWHhg0',
    featured: true,
    tech: ['YOLO', 'NVIDIA Jetson Nano', 'TensorRT', 'OpenCV', 'Python'],
  },
  {
    id: 2,
    title: 'AI-Powered Golf Ball Tracking & Trajectory Visualization',
    tag: 'Computer Vision',
    tagColor: '#3B6D11',
    metric: 'sub-frame precision',
    image: 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=800&q=80',
    problem:
      "Golf coaches and analysts struggle to track a golf ball's full flight because its high speed and small size make it nearly invisible to standard cameras. As a result, typical object detection models fail, golf balls often appear as blurred specks or disappear between frames, making accurate tracking and flight path analysis unreliable with existing solutions.",
    solution:
      "Engineered a custom computer vision pipeline specifically designed for high-speed, small-object tracking. Unlike generic detection models that drop fast-moving objects, our pipeline combines frame-level detection tuned for motion blur with predictive interpolation to capture the ball's exact position in every single frame — even under challenging lighting and background conditions. The trajectory is drawn as a real-time overlay on the video feed.",
    outcome:
      'Achieved frame-perfect ball capture throughout the entire flight path where standard models fail. Coaches can now analyze the full trajectory arc and shot angle with high precision, enabling data-driven performance coaching and tactical feedback that was previously only accessible with expensive professional broadcast setups.',
    youtubeId: 'h0EvraOMMQU',
    featured: false,
    tech: ['Custom Detection Pipeline', 'OpenCV', 'Python', 'Kalman Filter', 'NumPy'],
  },
  {
    id: 3,
    title: 'AI-Powered Safe Driving Assistant — Android App',
    tag: 'Mobile AI',
    tagColor: '#185FA5',
    metric: 'real-time alerts',
    image: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=800&q=80',
    problem:
      "Driver fatigue and inattention are among the leading causes of road accidents worldwide. Most drivers have no reliable way to monitor their own alertness in real time, and families have no visibility into a loved one's safety during long or late-night drives. Standard navigation apps track location but do nothing to detect drowsiness or distraction before an accident occurs.",
    solution:
      "Developed an Android application that uses the front camera to continuously monitor the driver's eyes in real time — detecting drowsiness, microsleeps, and loss of road attention through eye tracking and blink pattern analysis. The app simultaneously tracks vehicle speed and live GPS location in the background. When dangerous behavior is detected — such as eyes closing or prolonged inattention — the driver is immediately alerted via sound and vibration before an incident can occur.",
    outcome:
      'Delivered a proactive safety layer that catches fatigue and distraction before they turn fatal. In emergency scenarios, the app automatically notifies designated family contacts with the driver\'s real-time GPS location and speed, giving families peace of mind and enabling faster emergency response. The app runs fully on-device with no cloud dependency, ensuring low latency and privacy.',
    youtubeId: '3b8ta_faMcU',
    featured: false,
    tech: ['Android', 'MediaPipe', 'Eye Tracking', 'GPS', 'Kotlin', 'Firebase'],
  },
  {
    id: 4,
    title: 'AI-Powered Body Measurement & Size Recommendation System',
    tag: 'Computer Vision',
    tagColor: '#c60664',
    metric: 'instant measurements',
    image: 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=800&q=80',
    problem:
      'Online shoppers frequently receive ill-fitting clothes because standard size charts vary wildly across brands and regions. Without the ability to try on garments, customers rely on guesswork — leading to high return rates, wasted logistics costs, and poor shopping experiences. Shopping platforms lose revenue and trust every time a customer receives the wrong fit.',
    solution:
      'Built a computer vision system that takes a single image of the user and automatically extracts precise full-body measurements — including chest, waist, hip, shirt length, pant length, shoulder width, and more — without any manual input or physical measuring tape. Shopping platforms integrate it via API, and users get an instant, accurate size recommendation for any garment on the site.',
    outcome:
      'Delivered a seamless try-before-you-buy experience powered entirely by a single photo. Shopping platforms using the system saw a significant reduction in size-related returns and improved customer confidence at checkout. Users no longer need to consult confusing size charts — they simply upload a photo and receive their exact measurements and the right size across any brand, instantly.',
    youtubeId: '',
    demoImage: require('../../../src/assets/images/case_study_4.jpg'),
    featured: false,
    tech: ['MediaPipe', 'Pose Estimation', 'OpenCV', 'Python', 'REST API', 'Deep Learning'],
  },
  {
  id: 5,
  title: 'AI-Powered ID Card Detection — Android App',
  tag: 'Mobile AI',
  tagColor: '#185FA5',
  metric: 'on-device precision',
  image: 'https://images.unsplash.com/photo-1601684632403-4ce4a06d8429?q=80&w=1170',
  problem:
    "Enterprises handling identity verification face a recurring challenge: capturing ID cards accurately in real-world conditions. Poor lighting causes glare, shaky hands introduce blur, incorrect distances result in incomplete captures, and standard camera apps have no intelligence to guide the user or validate the card frame before capturing. For an MNC looking to integrate ID card scanning into their internal workflow, these issues meant unreliable captures and manual re-attempts — creating friction and slowing down operations.",
  solution:
    "Built a fully on-device Android application for ID card detection and capture, developed as a demo product for MNC integration. The app uses a custom-trained model built on an in-house dataset specifically curated for ID card detection. Real-time glare detection identifies reflection interference and prompts the user to adjust. Blur detection ensures the camera is stable before capture is triggered. Proximity detection guides the user to position the card correctly so the full ID fits within the frame. Once captured, perspective transformation is applied to correct any angular distortion, producing a clean, flat, document-ready output.",
  outcome:
    "Delivered a seamless, guided capture experience that eliminates the guesswork for end users. The app intelligently detects and communicates issues in real time, ensuring every capture meets quality standards before it is saved. The final output is a perspective-corrected, high-quality ID card image ready for downstream processing or verification pipelines. Running fully on-device with no cloud dependency, the solution ensures data privacy and works reliably in low-connectivity environments.",
  youtubeId: '7gB8Em23uFU',
  featured: false,
  tech: ['Android', 'Kotlin', 'CameraX', 'OpenCV', 'PyTorch Mobile', 'Perspective Transform', 'Custom Dataset'],
},
];

/* ─── Detail Page ─────────────────────────────────────────────── */
function DetailPage({ cs, onClose, onPrev, onNext, currentIndex, total }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onPrev, onNext, onClose]);

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < total - 1;

  // Shared handler: blur immediately so mobile browser won't scroll-to-focus
  const handleBtnFocus = (e) => e.target.blur();

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundColor: '#fbfbf5', color: '#1a1a1a' }}>
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 md:px-12 py-4 border-b" style={{ borderColor: 'rgba(26,26,26,0.1)' }}>
        <button
          onFocus={handleBtnFocus}
          onClick={onClose}
          className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest hover:opacity-60 transition-opacity font-tech"
          style={{ color: theme.primary }}
        >
          <ArrowLeft size={16} /> Back to Work
        </button>
        <span className="text-xs font-bold uppercase tracking-widest opacity-40">{currentIndex + 1} / {total}</span>
        <button onFocus={handleBtnFocus} onClick={onClose} className="p-2 hover:opacity-60 transition-opacity">
          <X size={20} />
        </button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
          <div className="mb-8">
            <span
              className="inline-block text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4"
              style={{ backgroundColor: `${theme.primary}15`, color: theme.primary }}
            >
              {cs.tag}
            </span>
            <h1 className="font-retropix text-2xl md:text-4xl leading-tight">{cs.title}</h1>
          </div>

          {cs.youtubeId ? (
            <div className="w-full aspect-video rounded-xl overflow-hidden mb-12 shadow-lg">
              <iframe
                src={`https://www.youtube.com/embed/${cs.youtubeId}`}
                title={cs.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          ) : cs.demoImage ? (
            <div className="w-full rounded-xl overflow-hidden mb-12 shadow-lg">
              <img src={cs.demoImage} alt={cs.title} className="w-full object-cover" />
            </div>
          ) : null}

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              { num: '01', label: 'The Problem', text: cs.problem, accent: false },
              { num: '02', label: 'The Solution', text: cs.solution, accent: false },
              { num: '03', label: 'The Outcome', text: cs.outcome, accent: true },
            ].map(({ num, label, text, accent }) => (
              <div key={num}>
                <p className="text-xs font-bold uppercase tracking-widest mb-3" style={accent ? { color: theme.primary } : { opacity: 0.4 }}>
                  {num}. {label}
                </p>
                <p className={`text-sm leading-relaxed ${accent ? 'font-bold' : ''}`}>{text}</p>
              </div>
            ))}
          </div>

          {cs.tech && (
            <div>
              <p className="text-xs font-bold uppercase tracking-widest mb-4 opacity-40">Tech Stack</p>
              <div className="flex flex-wrap gap-2">
                {cs.tech.map((t) => (
                  <span key={t} className="text-xs font-bold uppercase tracking-wider px-3 py-1 border" style={{ borderColor: 'rgba(26,26,26,0.2)', color: '#1a1a1a' }}>
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom nav */}
      <div className="flex items-center justify-between px-6 md:px-12 py-4 border-t" style={{ borderColor: 'rgba(26,26,26,0.1)' }}>
        <button
          onFocus={handleBtnFocus}
          onClick={onPrev}
          disabled={!hasPrev}
          className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest transition-opacity"
          style={{ color: theme.primary, opacity: hasPrev ? 1 : 0.25, cursor: hasPrev ? 'pointer' : 'not-allowed' }}
        >
          <ChevronLeft size={16} />
          <span className="hidden sm:inline">Previous</span>
          {hasPrev && (
            <span className="hidden md:inline text-xs font-normal normal-case tracking-normal opacity-60 ml-1 max-w-[160px] truncate" style={{ color: '#1a1a1a' }}>
              {caseStudies[currentIndex - 1].title}
            </span>
          )}
        </button>

        <div className="flex items-center gap-2">
          {caseStudies.map((_, i) => (
            <button
              key={i}
              onFocus={handleBtnFocus}
              onClick={() => { const diff = i - currentIndex; if (diff > 0) for (let d = 0; d < diff; d++) onNext(); else if (diff < 0) for (let d = 0; d < -diff; d++) onPrev(); }}
              style={{ width: i === currentIndex ? 24 : 8, height: 8, borderRadius: 4, backgroundColor: theme.primary, opacity: i === currentIndex ? 1 : 0.2, transition: 'width 0.3s ease, opacity 0.3s ease', border: 'none', padding: 0, cursor: 'pointer' }}
            />
          ))}
        </div>

        <button
          onFocus={handleBtnFocus}
          onClick={onNext}
          disabled={!hasNext}
          className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest transition-opacity"
          style={{ color: theme.primary, opacity: hasNext ? 1 : 0.25, cursor: hasNext ? 'pointer' : 'not-allowed' }}
        >
          {hasNext && (
            <span className="hidden md:inline text-xs font-normal normal-case tracking-normal opacity-60 mr-1 max-w-[160px] truncate" style={{ color: '#1a1a1a' }}>
              {caseStudies[currentIndex + 1].title}
            </span>
          )}
          <span className="hidden sm:inline">Next</span>
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

/* ─── Carousel Card ───────────────────────────────────────────── */
function CarouselCard({ cs, zIndex, toTheLeft, onClick }) {
  const getTransform = () => {
    if (zIndex === 2) return 'translateX(0) scale(1)';
    if (zIndex === 1) return `translateX(${toTheLeft ? '52%' : '-52%'}) scale(0.85)`;
    if (zIndex === 0) return `translateX(${toTheLeft ? '90%' : '-90%'}) scale(0.70)`;
    return `translateX(${toTheLeft ? '90%' : '-90%'}) scale(0.70)`;
  };

  const cardStyle = {
    position: 'absolute',
    width: '350px',
    cursor: zIndex === 2 ? 'pointer' : 'default',
    borderRadius: '16px',
    overflow: 'hidden',
    backgroundColor: '#fff',
    boxShadow: zIndex === 2
      ? '0 20px 60px rgba(0,0,0,0.18)'
      : '0 4px 20px rgba(0,0,0,0.08)',
    transform: getTransform(),
    zIndex: zIndex === -1 ? -1 : zIndex,
    opacity: zIndex === -1 ? 0 : zIndex === 0 ? 0.6 : 1,
    transition: 'all 400ms cubic-bezier(0.4, 0, 0.2, 1)',
    pointerEvents: zIndex === 2 ? 'auto' : 'none',
  };

  return (
    <div style={cardStyle} onClick={zIndex === 2 ? onClick : undefined}>
      <div style={{ height: '260px', overflow: 'hidden', position: 'relative' }}>
        <img
          src={cs.image}
          alt={cs.title}
          style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 500ms ease' }}
        />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 60%)' }} />
        <div
          style={{
            position: 'absolute', bottom: '12px', left: '12px',
            backgroundColor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)',
            color: '#fff', borderRadius: '999px', padding: '4px 12px',
            fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px',
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: theme.primary, display: 'inline-block' }} />
          {cs.metric}
        </div>
      </div>

      <div style={{ padding: '20px 24px 24px' }}>
        <span
          style={{
            display: 'inline-block', fontSize: '11px', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.1em',
            backgroundColor: `${theme.primary}12`, color: theme.primary,
            borderRadius: '999px', padding: '4px 14px', marginBottom: '10px',
          }}
        >
          {cs.tag}
        </span>
        <h3 style={{ fontSize: '16px', fontWeight: 700, lineHeight: 1.4, color: '#1a1a1a', marginBottom: '14px' }} className="font-retropix">
          {cs.title}
        </h3>
        {zIndex === 2 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: theme.primary }}>
            View Case Study <ChevronRight size={14} />
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Case Studies Carousel ───────────────────────────────────── */
const AUTO_INTERVAL = 3000;

function CaseStudiesCarousel({ onCardClick }) {
  const count = caseStudies.length;
  const [centerIdx, setCenterIdx] = useState(0);
  const [animating, setAnimating] = useState(false);
  const timerRef = useRef(null);
  const stageRef = useRef(null);
  const touchStartX = useRef(null);

  const advance = useCallback((dir) => {
    if (animating) return;
    setAnimating(true);
    setCenterIdx((prev) => (prev + dir + count) % count);
    setTimeout(() => setAnimating(false), 420);
  }, [animating, count]);

  // Auto-advance
  useEffect(() => {
    timerRef.current = setInterval(() => advance(1), AUTO_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [advance]);

  // Pause on hover
  const pauseAuto = () => clearInterval(timerRef.current);
  const resumeAuto = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => advance(1), AUTO_INTERVAL);
  };

  // Touch swipe — passive: false so we can call preventDefault
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;

    const onTouchStart = (e) => {
      touchStartX.current = e.touches[0].clientX;
      pauseAuto();
    };

    const onTouchMove = (e) => {
      // Prevent the page from scrolling while the user is swiping the carousel
      if (touchStartX.current !== null) {
        const dx = e.touches[0].clientX - touchStartX.current;
        if (Math.abs(dx) > 8) e.preventDefault();
      }
    };

    const onTouchEnd = (e) => {
      if (touchStartX.current === null) return;
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      if (Math.abs(dx) > 40) advance(dx < 0 ? 1 : -1);
      touchStartX.current = null;
      resumeAuto();
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false }); // must be non-passive to preventDefault
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [advance]); // eslint-disable-line react-hooks/exhaustive-deps

  function getSlotFor(idx) {
    const prev = (centerIdx - 1 + count) % count;
    const next = (centerIdx + 1) % count;
    const prevPrev = (centerIdx - 2 + count) % count;
    const nextNext = (centerIdx + 2) % count;

    if (idx === centerIdx) return { zIndex: 2, toTheLeft: false };
    if (idx === prev) return { zIndex: 1, toTheLeft: true };
    if (idx === next) return { zIndex: 1, toTheLeft: false };
    if (idx === prevPrev) return { zIndex: 0, toTheLeft: true };
    if (idx === nextNext) return { zIndex: 0, toTheLeft: false };
    return { zIndex: -1, toTheLeft: false };
  }

  // Shared: immediately blur button on focus so mobile won't scroll-to-element
  const handleBtnFocus = (e) => e.target.blur();

  return (
    <div onMouseEnter={pauseAuto} onMouseLeave={resumeAuto}>
      {/* Carousel stage */}
      <div
        ref={stageRef}
        style={{ position: 'relative', height: '460px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        {caseStudies.map((cs, i) => {
          const { zIndex, toTheLeft } = getSlotFor(i);
          return (
            <CarouselCard
              key={cs.id}
              cs={cs}
              zIndex={zIndex}
              toTheLeft={toTheLeft}
              onClick={() => onCardClick(i)}
            />
          );
        })}
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', marginTop: '32px' }}>
        {/* Prev button */}
        <button
          onFocus={handleBtnFocus}
          onClick={() => advance(-1)}
          style={{
            width: '40px', height: '40px', borderRadius: '50%',
            border: `2px solid ${theme.primary}`, background: 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', transition: 'background 0.2s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${theme.primary}15`}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          aria-label="Previous"
        >
          <ChevronLeft size={18} color={theme.primary} />
        </button>

        {/* Dot indicators */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {caseStudies.map((_, i) => (
            <button
              key={i}
              onFocus={handleBtnFocus}
              onClick={() => { if (!animating) { setAnimating(true); setCenterIdx(i); setTimeout(() => setAnimating(false), 420); } }}
              aria-label={`Go to slide ${i + 1}`}
              style={{
                width: i === centerIdx ? 28 : 8, height: 8,
                borderRadius: 4, backgroundColor: theme.primary,
                opacity: i === centerIdx ? 1 : 0.22,
                transition: 'width 0.3s ease, opacity 0.3s ease',
                border: 'none', padding: 0, cursor: 'pointer',
              }}
            />
          ))}
        </div>

        {/* Next button */}
        <button
          onFocus={handleBtnFocus}
          onClick={() => advance(1)}
          style={{
            width: '40px', height: '40px', borderRadius: '50%',
            border: `2px solid ${theme.primary}`, background: 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', transition: 'background 0.2s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${theme.primary}15`}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          aria-label="Next"
        >
          <ChevronRight size={18} color={theme.primary} />
        </button>
      </div>
    </div>
  );
}

/* ─── Portfolio Section ───────────────────────────────────────── */
export default function Portfolio() {
  const [activeIndex, setActiveIndex] = useState(null);

  return (
    <>
      <section id="portfolio" className="py-24" style={{ backgroundColor: 'white' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">

          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
            <div>
              <h2 className="font-retropix text-3xl md:text-4xl mb-4" style={{ color: theme.primary }}>
                Featured Work
              </h2>
              <p className="opacity-80 max-w-xl">
                How we turn complex challenges into deployed, scalable solutions.
              </p>
            </div>
            <a
              href="https://www.linkedin.com/company/miraai-sol/"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 text-sm font-bold uppercase hover:underline"
              style={{ color: theme.primary }}
            >
              More on LinkedIn <ChevronRight size={16} />
            </a>
          </div>

          {/* Carousel */}
          <CaseStudiesCarousel onCardClick={(i) => setActiveIndex(i)} />

        </div>
      </section>

      {/* Detail overlay */}
      {activeIndex !== null && (
        <DetailPage
          cs={caseStudies[activeIndex]}
          currentIndex={activeIndex}
          total={caseStudies.length}
          onClose={() => setActiveIndex(null)}
          onPrev={() => setActiveIndex((i) => Math.max(0, i - 1))}
          onNext={() => setActiveIndex((i) => Math.min(caseStudies.length - 1, i + 1))}
        />
      )}
    </>
  );
}